import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../../api/customers';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import CustomerForm from './CustomerForm';
import './CustomerManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',        label: 'All Customers', icon: '👥', filter: null },
  { id: 'active',     label: 'Active',        icon: '✅', filter: 'active' },
  { id: 'hasWallet',  label: 'Has Wallet',    icon: '💰', filter: 'hasWallet' },
  { id: 'hasCredit',  label: 'Has Credit',    icon: '🏦', filter: 'hasCredit' },
  { id: 'inactive',   label: 'Inactive',      icon: '⏸️', filter: 'inactive' },
];

// ─── Inline components ────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CustomerManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  // Tab + list state
  const [activeTab, setActiveTab]     = useState('all');
  const [customers, setCustomers]     = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [page, setPage]               = useState(1);
  const [pages, setPages]             = useState(1);
  const [loading, setLoading]         = useState(false);

  // Filters
  const [search, setSearch]           = useState('');
  const [filterWallet, setFilterWallet] = useState('');
  const [filterCredit, setFilterCredit] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd]     = useState('');

  // Summary
  const [summary, setSummary]         = useState(null);

  // Detail modal
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailLoading, setDetailLoading]       = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal]   = useState(false);
  const [editTarget, setEditTarget]         = useState(null);
  const [editLoading, setEditLoading]       = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading]     = useState(false);

  // Deactivate modal
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget]       = useState(null);
  const [deactivateLoading, setDeactivateLoading]     = useState(false);

  // ── Load summary ────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await customersAPI.getDashboardSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  // ── Load customers ──────────────────────────────────────────────────────────
  const loadCustomers = useCallback(async (resetPage = false) => {
    setLoading(true);
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    try {
      const params = {
        page: p,
        limit: 20,
        search: search || undefined,
        hasWallet: filterWallet || undefined,
        hasCredit: filterCredit || undefined,
        startDate: filterStart || undefined,
        endDate: filterEnd || undefined,
      };

      const tabFilter = TABS.find(t => t.id === activeTab)?.filter;
      if (tabFilter === 'active') params.isActive = 'true';
      else if (tabFilter === 'inactive') params.isActive = 'false';
      else if (tabFilter === 'hasWallet') params.hasWallet = 'true';
      else if (tabFilter === 'hasCredit') params.hasCredit = 'true';

      const res = await customersAPI.getAll(params);
      setCustomers(res.data);
      setTotalCount(res.pagination.total);
      setPages(res.pagination.pages);
    } catch {
      showNotification('Failed to load customers', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, filterWallet, filterCredit, filterStart, filterEnd]);

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadCustomers(true); }, [activeTab, search, filterWallet, filterCredit, filterStart, filterEnd]);
  useEffect(() => { if (page > 0) loadCustomers(); }, [page]);

  // ── Open detail ─────────────────────────────────────────────────────────────
  const openDetail = async (customer) => {
    setSelectedCustomer(customer);
    setDetailLoading(true);
    try {
      const res = await customersAPI.getById(customer._id);
      setSelectedCustomer(res.data);
    } catch {
      showNotification('Failed to load customer details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Create customer ─────────────────────────────────────────────────────────
  const handleCreate = async (data) => {
    setCreateLoading(true);
    try {
      await customersAPI.create(data);
      showNotification('Customer created successfully', 'success');
      setShowCreateModal(false);
      loadCustomers();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to create customer', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit customer ───────────────────────────────────────────────────────────
  const openEditModal = (customer, e) => {
    e?.stopPropagation();
    setEditTarget(customer);
    setShowEditModal(true);
  };

  const handleEdit = async (data) => {
    setEditLoading(true);
    try {
      await customersAPI.update(editTarget._id, data);
      showNotification('Customer updated successfully', 'success');
      setShowEditModal(false);
      setEditTarget(null);
      loadCustomers();
      loadSummary();
      if (selectedCustomer?._id === editTarget._id) {
        const res = await customersAPI.getById(editTarget._id);
        setSelectedCustomer(res.data);
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update customer', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Deactivate ──────────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await customersAPI.deactivate(deactivateTarget._id);
      showNotification('Customer deactivated', 'success');
      setShowDeactivateModal(false);
      setDeactivateTarget(null);
      if (selectedCustomer?._id === deactivateTarget._id) setSelectedCustomer(null);
      loadCustomers();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to deactivate customer', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const tabCount = (filterId) => {
    if (!summary || !filterId) return null;
    if (filterId === 'active') return summary.activeCount;
    if (filterId === 'inactive') return summary.inactiveCount;
    if (filterId === 'hasWallet') return summary.withWalletCount;
    if (filterId === 'hasCredit') return summary.withCreditCount;
    return null;
  };

  const clearFilters = () => {
    setFilterWallet(''); setFilterCredit(''); setFilterStart(''); setFilterEnd('');
  };

  const hasFilters = filterWallet || filterCredit || filterStart || filterEnd;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Customer Management</h1>
          <p className="ps-subtitle">Manage customer information, wallets and credit</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>+ New Customer</Button>
      </div>

      {/* ── Summary Cards ── */}
      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="👥" label="Total Customers" accent="#3b82f6"
            value={summary.totalCount ?? 0}
            sub={`${summary.activeCount ?? 0} active`}
          />
          <SummaryCard
            icon="💰" label="Total Wallet Balance" accent="#10b981"
            value={formatCurrency(summary.totalWalletBalance ?? 0)}
            sub={`${summary.withWalletCount ?? 0} customers`}
          />
          <SummaryCard
            icon="🏦" label="Credit Outstanding" accent="#f59e0b"
            value={formatCurrency(summary.totalCreditUsed ?? 0)}
            sub={`${summary.withCreditCount ?? 0} customers`}
          />
          <SummaryCard
            icon="📊" label="Avg Orders" accent="#8b5cf6"
            value={(summary.avgOrdersPerCustomer ?? 0).toFixed(1)}
            sub="per customer"
          />
          <SummaryCard
            icon="⭐" label="Loyalty Points" accent="#f59e0b"
            value={summary.totalLoyaltyPoints ?? 0}
            sub="across all customers"
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setPage(1); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.filter && tabCount(tab.filter) != null && (
              <span className="om-tab-count">{tabCount(tab.filter)}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Filters */}
          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by name, phone or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="om-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <select className="ps-filter-select" value={filterWallet} onChange={e => setFilterWallet(e.target.value)}>
              <option value="">All Wallet</option>
              <option value="true">Has Balance</option>
              <option value="false">No Balance</option>
            </select>
            <select className="ps-filter-select" value={filterCredit} onChange={e => setFilterCredit(e.target.value)}>
              <option value="">All Credit</option>
              <option value="true">Has Credit</option>
              <option value="false">No Credit</option>
            </select>
            <input type="date" className="ps-filter-date" value={filterStart} onChange={e => setFilterStart(e.target.value)} placeholder="From" />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>→</span>
            <input type="date" className="ps-filter-date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} placeholder="To" />
            {hasFilters && (
              <button className="om-clear-filters" onClick={clearFilters}>Clear filters ✕</button>
            )}
            <Button variant="secondary" onClick={() => { loadCustomers(); loadSummary(); }}>↺</Button>
          </div>

          {/* Table meta */}
          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} customer{totalCount !== 1 ? 's' : ''}</span>
            {summary && activeTab === 'all' && (
              <span>
                Total spent: <strong style={{ color: '#10b981' }}>{formatCurrency(summary.totalSpent ?? 0)}</strong>
              </span>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="ps-loading">Loading customers…</div>
          ) : (
            <>
              <div className="ps-table-wrap">
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Orders</th>
                      <th>Total Spent</th>
                      <th>Wallet</th>
                      <th>Credit</th>
                      <th>Loyalty</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="ps-empty">
                          <div style={{ fontSize: 36, marginBottom: 8 }}>👤</div>
                          No customers found
                        </td>
                      </tr>
                    ) : customers.map(cust => {
                      const wallet = cust.walletBalance || 0;
                      const hasCredit = cust.creditLimit > 0;
                      const creditUsed = cust.creditUsed || 0;
                      const creditLimit = cust.creditLimit || 0;
                      const creditPct = hasCredit ? Math.round((creditUsed / creditLimit) * 100) : 0;

                      return (
                        <tr key={cust._id} className="ps-row" onClick={() => openDetail(cust)}>
                          {/* Customer */}
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="om-avatar-sm">
                                {cust.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="om-cust-name-cell">{cust.name}</div>
                                {cust.loyaltyPoints > 0 && (
                                  <span className="cm-loyalty-chip">⭐ {cust.loyaltyPoints} pts</span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Contact */}
                          <td>
                            <div style={{ fontSize: 13, color: '#374151' }}>{formatPhone(cust.phone)}</div>
                            {cust.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{cust.email}</div>}
                          </td>

                          {/* Orders */}
                          <td style={{ fontWeight: 600, color: '#374151' }}>{cust.totalOrders ?? 0}</td>

                          {/* Total Spent */}
                          <td style={{ fontWeight: 800, color: '#0f172a' }}>
                            {formatCurrency(cust.totalSpent ?? 0)}
                          </td>

                          {/* Wallet */}
                          <td>
                            <span className="cm-finance-chip green" style={{ opacity: wallet > 0 ? 1 : 0.4 }}>
                              💰 {formatCurrency(wallet)}
                            </span>
                          </td>

                          {/* Credit */}
                          <td>
                            {!hasCredit ? (
                              <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                            ) : (
                              <div style={{ minWidth: 100 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                                  <span style={{ fontWeight: 600, color: creditPct > 80 ? '#ef4444' : '#374151' }}>
                                    {formatCurrency(creditUsed)}
                                  </span>
                                  <span style={{ color: '#9ca3af' }}>{formatCurrency(creditLimit)}</span>
                                </div>
                                <div className="cm-credit-bar-wrap">
                                  <div
                                    className="cm-credit-bar-fill"
                                    style={{
                                      width: `${creditPct}%`,
                                      background: creditPct > 80 ? '#ef4444' : creditPct > 50 ? '#f59e0b' : '#10b981'
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Loyalty */}
                          <td style={{ color: '#f59e0b', fontWeight: 600 }}>{cust.loyaltyPoints ?? 0}</td>

                          {/* Status */}
                          <td>
                            <span className="cm-status-badge" style={cust.isActive
                              ? { background: '#d1fae5', color: '#065f46' }
                              : { background: '#fee2e2', color: '#991b1b' }
                            }>
                              {cust.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>

                          {/* Joined */}
                          <td className="ps-date">{formatDateTime(cust.createdAt)}</td>

                          {/* Actions */}
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                className="om-action-btn"
                                onClick={e => openEditModal(cust, e)}
                                title="Edit customer"
                              >
                                ✏️
                              </button>
                              {cust.isActive && (
                                <button
                                  className="om-action-btn danger"
                                  onClick={e => { e.stopPropagation(); setDeactivateTarget(cust); setShowDeactivateModal(true); }}
                                  title="Deactivate"
                                >
                                  ⏸️
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="ps-pagination">
                  <Button variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="ps-page-label">Page {page} of {pages}</span>
                  <Button variant="secondary" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CUSTOMER DETAIL MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {selectedCustomer && (
        <Modal
          isOpen={!!selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          title={selectedCustomer.name}
          size="large"
        >
          {detailLoading ? (
            <div className="ps-loading">Loading details…</div>
          ) : (
            <CustomerDetailContent
              customer={selectedCustomer}
              onEdit={(e) => openEditModal(selectedCustomer, e)}
              formatCurrency={formatCurrency}
              formatDateTime={formatDateTime}
              formatPhone={formatPhone}
            />
          )}
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          CREATE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Customer"
      >
        <CustomerForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          showCreditLimit={true}
        />
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          EDIT MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditTarget(null); }}
        title="Edit Customer"
      >
        {editTarget && (
          <CustomerForm
            customer={editTarget}
            onSubmit={handleEdit}
            onCancel={() => { setShowEditModal(false); setEditTarget(null); }}
            showCreditLimit={true}
          />
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          DEACTIVATE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}
        title="Deactivate Customer"
      >
        {deactivateTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Deactivate {deactivateTarget.name}?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  This will mark the customer as inactive. They will not appear in active customer lists, but their data will be preserved.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeactivate} disabled={deactivateLoading}>
                {deactivateLoading ? 'Deactivating…' : 'Deactivate Customer'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Customer Detail Content ──────────────────────────────────────────────────
const CustomerDetailContent = ({ customer, onEdit, formatCurrency, formatDateTime, formatPhone }) => {
  const wallet = customer.walletBalance || 0;
  const hasCredit = customer.creditLimit > 0;
  const creditUsed = customer.creditUsed || 0;
  const creditLimit = customer.creditLimit || 0;
  const creditAvail = Math.max(0, creditLimit - creditUsed);
  const creditPct = hasCredit ? Math.round((creditUsed / creditLimit) * 100) : 0;

  return (
    <div className="cm-detail">

      {/* Top bar */}
      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <span className="cm-status-badge" style={customer.isActive
            ? { background: '#d1fae5', color: '#065f46' }
            : { background: '#fee2e2', color: '#991b1b' }
          }>
            {customer.isActive ? '✅ Active' : '⏸️ Inactive'}
          </span>
          {wallet > 0 && (
            <span className="cm-finance-chip green">💰 {formatCurrency(wallet)}</span>
          )}
          {hasCredit && (
            <span className="cm-finance-chip amber">🏦 {formatCurrency(creditAvail)} available</span>
          )}
          {customer.loyaltyPoints > 0 && (
            <span className="cm-finance-chip purple">⭐ {customer.loyaltyPoints} pts</span>
          )}
        </div>
        <button className="ps-settle-btn-lg" onClick={onEdit}>Edit Customer</button>
      </div>

      {/* 3-column info */}
      <div className="cm-detail-grid">

        {/* Contact Info */}
        <div className="om-detail-section">
          <div className="om-section-title">Contact Information</div>
          {[
            ['Phone',     formatPhone(customer.phone)],
            ['Email',     customer.email || '—'],
            ...(customer.address?.area ? [['Area', customer.address.area]] : []),
            ...(customer.address?.block ? [['Block', customer.address.block]] : []),
            ...(customer.address?.street ? [['Street', customer.address.street]] : []),
            ...(customer.address?.building ? [['Building', customer.address.building]] : []),
            ...(customer.address?.floor ? [['Floor', customer.address.floor]] : []),
            ...(customer.address?.apartment ? [['Apartment', customer.address.apartment]] : []),
            ...(customer.civilId ? [['Civil ID', customer.civilId]] : []),
          ].map(([k, v]) => (
            <div key={k} className="om-info-row">
              <span className="om-info-label">{k}</span>
              <span className="om-info-val">{v}</span>
            </div>
          ))}
          {customer.notes && (
            <div className="om-notes-box" style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Notes</span>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151' }}>{customer.notes}</p>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="om-detail-section">
          <div className="om-section-title">Customer Stats</div>
          <div className="cm-stats-grid">
            <div className="cm-stat-box">
              <span className="cm-stat-icon">📦</span>
              <div>
                <div className="cm-stat-value">{customer.totalOrders ?? 0}</div>
                <div className="cm-stat-label">Total Orders</div>
              </div>
            </div>
            <div className="cm-stat-box">
              <span className="cm-stat-icon">💵</span>
              <div>
                <div className="cm-stat-value">{formatCurrency(customer.totalSpent ?? 0)}</div>
                <div className="cm-stat-label">Total Spent</div>
              </div>
            </div>
            <div className="cm-stat-box">
              <span className="cm-stat-icon">⭐</span>
              <div>
                <div className="cm-stat-value">{customer.loyaltyPoints ?? 0}</div>
                <div className="cm-stat-label">Loyalty Points</div>
              </div>
            </div>
            <div className="cm-stat-box">
              <span className="cm-stat-icon">📅</span>
              <div>
                <div className="cm-stat-value" style={{ fontSize: 13 }}>
                  {formatDateTime(customer.createdAt).split(',')[0]}
                </div>
                <div className="cm-stat-label">Member Since</div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="om-detail-section">
          <div className="om-section-title">Financial Summary</div>
          
          {/* Wallet */}
          <div className="cm-finance-card wallet">
            <div className="cm-finance-header">
              <span className="cm-finance-icon">💰</span>
              <div>
                <div className="cm-finance-title">Wallet Balance</div>
                <div className="cm-finance-amount" style={{ color: wallet > 0 ? '#10b981' : '#9ca3af' }}>
                  {formatCurrency(wallet)}
                </div>
              </div>
            </div>
          </div>

          {/* Credit */}
          {hasCredit ? (
            <div className="cm-finance-card credit">
              <div className="cm-finance-header">
                <span className="cm-finance-icon">🏦</span>
                <div>
                  <div className="cm-finance-title">Credit Account</div>
                  <div className="cm-finance-amount" style={{ color: creditAvail > 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(creditAvail)} available
                  </div>
                </div>
              </div>
              <div className="cm-credit-breakdown">
                <div className="cm-credit-item">
                  <span>Used</span>
                  <strong style={{ color: creditPct > 80 ? '#ef4444' : '#f59e0b' }}>
                    {formatCurrency(creditUsed)}
                  </strong>
                </div>
                <div className="cm-credit-item">
                  <span>Limit</span>
                  <strong>{formatCurrency(creditLimit)}</strong>
                </div>
              </div>
              <div className="cm-credit-bar-wrap">
                <div
                  className="cm-credit-bar-fill"
                  style={{
                    width: `${creditPct}%`,
                    background: creditPct > 80 ? '#ef4444' : creditPct > 50 ? '#f59e0b' : '#10b981'
                  }}
                />
              </div>
              <div className="cm-credit-pct">{creditPct}% used</div>
            </div>
          ) : (
            <div className="cm-finance-card disabled">
              <div className="cm-finance-header">
                <span className="cm-finance-icon">🏦</span>
                <div>
                  <div className="cm-finance-title">Credit Account</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Not enabled for this customer</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent activity note */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0',
        borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#64748b', textAlign: 'center'
      }}>
        💡 View full order history and transaction details on the dedicated customer detail page
      </div>
    </div>
  );
};

export default CustomerManagement;
